import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query"
import { api, unwrap } from "./client"
import type {
  Account,
  AccountPatch,
  CabinetsAssignment,
  AdminAccountsQuery,
  AdminEntitiesQuery,
  AdminNbrOldQuery,
  AdminNbrOldSuggestQuery,
  AuditEntry,
  Citation,
  CitationStyle,
  Collection,
  CollectionCreate,
  CollectionDetail,
  ContentPage,
  Dictionaries,
  DuplicateCandidate,
  EditionWrite,
  EntityCard,
  EntityContext,
  EntityMatch,
  EntityMeta,
  EntityRef,
  EntitySummary,
  EntityType,
  EntityWrite,
  ErrorReport,
  ErrorReportCreate,
  ErrorReportStatus,
  Identifier,
  IdentifierWrite,
  ImportJob,
  MarcView,
  NbrOldList,
  NbrOldPerson,
  NbrOldSuggestion,
  Nomen,
  NomenWrite,
  RecommendationGroup,
  Relation,
  RelationWrite,
  SavedQuery,
  SavedQueryCreate,
  SearchQuery,
  SearchResponse,
  SearchableType,
  SparqlResults,
  Stats,
  UserProfile,
  UserRole,
} from "./types"

// ------------------------------------------------------------------ keys
export const queryKeys = {
  search: (params: SearchQuery) => ["search", params] as const,
  autocomplete: (q: string, type?: EntityType, limit?: number) => ["autocomplete", q, type ?? null, limit ?? null] as const,
  stats: ["stats"] as const,
  entity: (type: EntityType, id: string) => ["entity", type, id] as const,
  entityContext: (type: EntityType, id: string) => ["entity-context", type, id] as const,
  entityMeta: (type: EntityType, id: string) => ["entity-meta", type, id] as const,
  citation: (type: EntityType, id: string, style: CitationStyle) => ["citation", type, id, style] as const,
  marc: (type: EntityType, id: string) => ["marc", type, id] as const,
  recommendations: (type: EntityType, id: string) => ["recommendations", type, id] as const,
  news: (page: number, pageSize: number) => ["news", page, pageSize] as const,
  page: (slug: string) => ["page", slug] as const,
  me: ["me"] as const,
  workspaces: ["workspaces"] as const,
  collections: ["collections"] as const,
  collection: (id: string) => ["collection", id] as const,
  savedQueries: ["saved-queries"] as const,
  adminAccounts: (params: AdminAccountsQuery) => ["admin", "accounts", params] as const,
  adminEntities: (params: AdminEntitiesQuery) => ["admin", "entities", params] as const,
  adminNbrOld: (params: AdminNbrOldQuery) => ["admin", "nbr-old", params] as const,
  adminNbrOldPerson: (label: string) => ["admin", "nbr-old", "person", label] as const,
  adminNbrOldSuggest: (params: AdminNbrOldSuggestQuery) => ["admin", "nbr-old", "suggest", params] as const,
  adminEntity: (id: string) => ["admin", "entity", id] as const,
  adminDictionaries: ["admin", "dictionaries"] as const,
  adminDuplicates: (type?: EntityType, threshold?: number) => ["admin", "duplicates", type ?? null, threshold ?? null] as const,
  adminEntityMatches: (params: { type?: EntityType; q?: string; scheme?: string; value?: string }) =>
    ["admin", "entity-matches", params.type ?? null, params.q ?? null, params.scheme ?? null, params.value ?? null] as const,
  adminErrorReports: (status: ErrorReportStatus | undefined, page: number) => ["admin", "error-reports", status ?? null, page] as const,
  adminImports: ["admin", "imports"] as const,
  adminImport: (id: string) => ["admin", "import", id] as const,
  adminAudit: (page: number, entityId?: string) => ["admin", "audit", page, entityId ?? null] as const,
}

// ---------------------------------------------------------------- public
export const useSearch = (params: SearchQuery, enabled = true) =>
  useQuery({
    queryKey: queryKeys.search(params),
    queryFn: () => unwrap<SearchResponse>(api.GET("/search", { params: { query: params as never } })),
    placeholderData: keepPreviousData,
    enabled,
  })

export const fetchAutocomplete = async (q: string, type?: EntityType, limit = 10): Promise<EntityRef[]> => {
  if (!q.trim()) return []
  const data = await unwrap<{ items: EntityRef[] }>(
    api.GET("/autocomplete", { params: { query: { q, type, limit } } }),
  )
  return data.items
}

export const useAutocomplete = (q: string, type?: EntityType, limit = 8) =>
  useQuery({
    queryKey: queryKeys.autocomplete(q, type, limit),
    queryFn: () => fetchAutocomplete(q, type, limit),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  })

export const useStats = () =>
  useQuery({
    queryKey: queryKeys.stats,
    queryFn: () => unwrap<Stats>(api.GET("/stats")),
    staleTime: 5 * 60_000,
  })

export const useEntity = (type: EntityType | undefined, id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.entity(type as EntityType, id as string),
    queryFn: () =>
      unwrap<EntityCard>(api.GET("/{type}/{id}", { params: { path: { type: type as EntityType, id: id as string } } })),
    enabled: Boolean(type && id),
  })

/** Карточки соседей для раскрытия эго-графа: те же ключи, что `useEntity`. */
export const useEntities = (refs: Array<{ type: EntityType; id: string }>) =>
  useQueries({
    queries: refs.map((ref) => ({
      queryKey: queryKeys.entity(ref.type, ref.id),
      queryFn: () =>
        unwrap<EntityCard>(api.GET("/{type}/{id}", { params: { path: { type: ref.type, id: ref.id } } })),
      enabled: Boolean(ref.type && ref.id),
    })),
  })

export const useEntityContext = (type: EntityType | undefined, id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: queryKeys.entityContext(type as EntityType, id as string),
    queryFn: () =>
      unwrap<EntityContext>(
        api.GET("/{type}/{id}/context" as never, {
          params: { path: { type: type as EntityType, id: id as string } },
        } as never) as never,
      ),
    enabled: Boolean(type && id) && enabled,
  })

export const useEntityMeta = (type: EntityType | undefined, id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.entityMeta(type as EntityType, id as string),
    queryFn: () =>
      unwrap<EntityMeta>(
        api.GET("/meta/{type}/{id}", { params: { path: { type: type as EntityType, id: id as string } } }),
      ),
    enabled: Boolean(type && id),
    staleTime: 10 * 60_000,
  })

export const useCitation = (type: EntityType, id: string, style: CitationStyle) =>
  useQuery({
    queryKey: queryKeys.citation(type, id, style),
    queryFn: () =>
      unwrap<Citation>(api.GET("/{type}/{id}/citation", { params: { path: { type, id }, query: { style } } })),
    staleTime: 10 * 60_000,
  })

export const useMarcView = (type: EntityType, id: string, enabled = true) =>
  useQuery({
    queryKey: queryKeys.marc(type, id),
    queryFn: () => unwrap<MarcView>(api.GET("/{type}/{id}/marc", { params: { path: { type, id } } })),
    enabled,
    staleTime: 10 * 60_000,
  })

export const useRecommendations = (type: EntityType, id: string, limit = 8) =>
  useQuery({
    queryKey: queryKeys.recommendations(type, id),
    queryFn: () =>
      unwrap<{ groups: RecommendationGroup[] }>(
        api.GET("/{type}/{id}/recommendations", { params: { path: { type, id }, query: { limit } } }),
      ),
    staleTime: 5 * 60_000,
  })

export const useNews = (page = 1, pageSize = 10) =>
  useQuery({
    queryKey: queryKeys.news(page, pageSize),
    queryFn: () =>
      unwrap<{ items: ContentPage[]; total: number }>(
        api.GET("/content/news", { params: { query: { page, page_size: pageSize } } }),
      ),
    placeholderData: keepPreviousData,
  })

export const useContentPage = (slug: string, options?: Pick<UseQueryOptions<ContentPage>, "retry">) =>
  useQuery({
    queryKey: queryKeys.page(slug),
    queryFn: () => unwrap<ContentPage>(api.GET("/content/pages/{slug}", { params: { path: { slug } } })),
    retry: options?.retry ?? false,
  })

export const useCreateErrorReport = () =>
  useMutation({
    mutationFn: (body: ErrorReportCreate) => unwrap<ErrorReport>(api.POST("/error-reports", { body })),
  })

export const runSparql = (query: string) =>
  unwrap<SparqlResults>(
    api.POST("/sparql", {
      body: query as never,
      bodySerializer: (body: unknown) => body as string,
      headers: { "Content-Type": "application/sparql-query", Accept: "application/sparql-results+json" },
    }) as never,
  )

export const useSparqlMutation = () => useMutation({ mutationFn: runSparql })

// -------------------------------------------------------------------- me
export const useMe = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.me,
    queryFn: () => unwrap<UserProfile>(api.GET("/me")),
    enabled,
    staleTime: 5 * 60_000,
  })

export const useWorkspaces = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: () => unwrap<{ items: import("./types").Workspace[] }>(api.GET("/me/workspaces")),
    enabled,
    staleTime: 5 * 60_000,
  })

export const useCollections = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.collections,
    queryFn: () => unwrap<{ items: Collection[] }>(api.GET("/me/collections")),
    enabled,
  })

export const useCollection = (collectionId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.collection(collectionId as string),
    queryFn: () =>
      unwrap<CollectionDetail>(
        api.GET("/me/collections/{collectionId}", { params: { path: { collectionId: collectionId as string } } }),
      ),
    enabled: Boolean(collectionId),
  })

export const useCreateCollection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CollectionCreate) => unwrap<Collection>(api.POST("/me/collections", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.collections }),
  })
}

export const useUpdateCollection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, body }: { collectionId: string; body: CollectionCreate }) =>
      unwrap<Collection>(api.PATCH("/me/collections/{collectionId}", { params: { path: { collectionId } }, body })),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.collections })
      qc.invalidateQueries({ queryKey: queryKeys.collection(vars.collectionId) })
    },
  })
}

export const useDeleteCollection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (collectionId: string) =>
      unwrap<void>(api.DELETE("/me/collections/{collectionId}", { params: { path: { collectionId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.collections }),
  })
}

export const useAddCollectionItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, entityId, note }: { collectionId: string; entityId: string; note?: string }) =>
      unwrap<CollectionDetail>(
        api.POST("/me/collections/{collectionId}/items", {
          params: { path: { collectionId } },
          body: { entity_id: entityId, note },
        }),
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.collections })
      qc.invalidateQueries({ queryKey: queryKeys.collection(vars.collectionId) })
    },
  })
}

export const useRemoveCollectionItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ collectionId, entityId }: { collectionId: string; entityId: string }) =>
      unwrap<void>(
        api.DELETE("/me/collections/{collectionId}/items/{entityId}", {
          params: { path: { collectionId, entityId } },
        }),
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.collections })
      qc.invalidateQueries({ queryKey: queryKeys.collection(vars.collectionId) })
    },
  })
}

export const useSavedQueries = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.savedQueries,
    queryFn: () => unwrap<{ items: SavedQuery[] }>(api.GET("/me/saved-queries")),
    enabled,
  })

export const useCreateSavedQuery = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SavedQueryCreate) => unwrap<SavedQuery>(api.POST("/me/saved-queries", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.savedQueries }),
  })
}

export const useUpdateSavedQuery = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ queryId, body }: { queryId: string; body: SavedQueryCreate }) =>
      unwrap<SavedQuery>(api.PATCH("/me/saved-queries/{queryId}", { params: { path: { queryId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.savedQueries }),
  })
}

export const useDeleteSavedQuery = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (queryId: string) =>
      unwrap<void>(api.DELETE("/me/saved-queries/{queryId}", { params: { path: { queryId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.savedQueries }),
  })
}

// ----------------------------------------------------------------- admin
export const useAdminAccounts = (params: AdminAccountsQuery) =>
  useQuery({
    queryKey: queryKeys.adminAccounts(params),
    queryFn: () =>
      unwrap<{ items: Account[]; total: number }>(
        api.GET("/admin/accounts", { params: { query: params } }),
      ),
    placeholderData: keepPreviousData,
  })

export const useAdminUpdateAccount = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, body }: { accountId: string; body: AccountPatch }) =>
      unwrap<Account>(api.PATCH("/admin/accounts/{accountId}", { params: { path: { accountId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "accounts"] }),
  })
}

export const useAdminReplaceAccountRoles = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, roles }: { accountId: string; roles: UserRole[] }) =>
      unwrap<Account>(
        api.PUT("/admin/accounts/{accountId}/roles", {
          params: { path: { accountId } },
          body: { roles },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "accounts"] }),
  })
}

export const useAdminReplaceAccountCabinets = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, cabinets }: { accountId: string; cabinets: CabinetsAssignment }) =>
      unwrap<Account>(
        // Path may be absent in nbr-api@main until cabinets contract lands.
        api.PUT("/admin/accounts/{accountId}/cabinets" as never, {
          params: { path: { accountId } },
          body: { cabinets },
        } as never),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "accounts"] }),
  })
}

export const useAdminEntities = (params: AdminEntitiesQuery) =>
  useQuery({
    queryKey: queryKeys.adminEntities(params),
    queryFn: () =>
      unwrap<{ items: EntitySummary[]; total: number }>(api.GET("/admin/entities", { params: { query: params } })),
    placeholderData: keepPreviousData,
  })

export const useAdminNbrOld = (params: AdminNbrOldQuery, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.adminNbrOld(params),
    queryFn: () => unwrap<NbrOldList>(api.GET("/admin/nbr-old", { params: { query: params } })),
    placeholderData: keepPreviousData,
    enabled,
  })

export const useAdminNbrOldPerson = (label: string | undefined) =>
  useQuery({
    queryKey: queryKeys.adminNbrOldPerson(label ?? ""),
    queryFn: () =>
      unwrap<NbrOldPerson>(api.GET("/admin/nbr-old/person", { params: { query: { label: label ?? "" } } })),
    enabled: Boolean(label),
    staleTime: 5 * 60_000,
  })

export const useAdminNbrOldSuggest = (params: AdminNbrOldSuggestQuery, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.adminNbrOldSuggest(params),
    queryFn: () =>
      unwrap<{ items: NbrOldSuggestion[] }>(api.GET("/admin/nbr-old/suggest", { params: { query: params } })),
    enabled,
    staleTime: 5 * 60_000,
  })

export const useAdminEntity = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.adminEntity(id as string),
    queryFn: () => unwrap<EntityCard>(api.GET("/admin/entities/{id}", { params: { path: { id: id as string } } })),
    enabled: Boolean(id),
  })

const invalidateEntity = (qc: ReturnType<typeof useQueryClient>, id?: string) => {
  qc.invalidateQueries({ queryKey: ["admin", "entities"] })
  qc.invalidateQueries({ queryKey: ["entity"] })
  if (id) qc.invalidateQueries({ queryKey: queryKeys.adminEntity(id) })
}

export const useAdminCreateEntity = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EntityWrite) => unwrap<EntityCard>(api.POST("/admin/entities", { body })),
    onSuccess: () => invalidateEntity(qc),
  })
}

export const useAdminCreateEdition = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditionWrite) => unwrap<EntityCard>(api.POST("/admin/editions", { body })),
    onSuccess: (card) => invalidateEntity(qc, card.id),
  })
}

export const useAdminUpdateEntity = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EntityWrite }) =>
      unwrap<EntityCard>(api.PATCH("/admin/entities/{id}", { params: { path: { id } }, body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.id),
  })
}

export const useAdminDeleteEntity = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap<void>(api.DELETE("/admin/entities/{id}", { params: { path: { id } } })),
    onSuccess: (_d, id) => invalidateEntity(qc, id),
  })
}

export const useAdminAddNomen = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: NomenWrite }) =>
      unwrap<Nomen>(api.POST("/admin/entities/{id}/nomens", { params: { path: { id } }, body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.id),
  })
}

export const useAdminUpdateNomen = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ nomenId, body }: { nomenId: string; body: NomenWrite; entityId: string }) =>
      unwrap<Nomen>(api.PATCH("/admin/nomens/{nomenId}", { params: { path: { nomenId } }, body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminDeleteNomen = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ nomenId }: { nomenId: string; entityId: string }) =>
      unwrap<void>(api.DELETE("/admin/nomens/{nomenId}", { params: { path: { nomenId } } })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminAddIdentifier = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IdentifierWrite }) =>
      unwrap<Identifier>(api.POST("/admin/entities/{id}/identifiers", { params: { path: { id } }, body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.id),
  })
}

export const useAdminDeleteIdentifier = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ identifierId }: { identifierId: string; entityId: string }) =>
      unwrap<void>(api.DELETE("/admin/identifiers/{identifierId}", { params: { path: { identifierId } } })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminCreateRelation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ body }: { body: RelationWrite; entityId: string }) =>
      unwrap<Relation>(api.POST("/admin/relations", { body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminUpdateRelation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ relationId, body }: { relationId: string; body: RelationWrite; entityId: string }) =>
      unwrap<Relation>(api.PATCH("/admin/relations/{relationId}", { params: { path: { relationId } }, body })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminDeleteRelation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ relationId }: { relationId: string; entityId: string }) =>
      unwrap<void>(api.DELETE("/admin/relations/{relationId}", { params: { path: { relationId } } })),
    onSuccess: (_d, vars) => invalidateEntity(qc, vars.entityId),
  })
}

export const useAdminMerge = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { target_id: string; source_ids: string[] }) =>
      unwrap<EntityCard>(api.POST("/admin/merge", { body })),
    onSuccess: () => {
      invalidateEntity(qc)
      qc.invalidateQueries({ queryKey: ["admin", "duplicates"] })
    },
  })
}

export const useAdminDuplicates = (type?: EntityType, threshold = 0.6, limit = 50) =>
  useQuery({
    queryKey: queryKeys.adminDuplicates(type, threshold),
    queryFn: () =>
      unwrap<{ items: DuplicateCandidate[] }>(
        api.GET("/admin/duplicates", { params: { query: { type, threshold, limit } } }),
      ),
  })

export type EntityMatchQuery = {
  type: EntityType
  q?: string
  scheme?: string
  value?: string
  limit?: number
}

export const fetchEntityMatches = async (params: EntityMatchQuery): Promise<EntityMatch[]> => {
  const q = params.q?.trim()
  const scheme = params.scheme?.trim()
  const value = params.value?.trim()
  if (!q && !(scheme && value)) return []
  const data = await unwrap<{ items: EntityMatch[] }>(
    api.GET("/admin/entities/matches", {
      params: { query: { type: params.type, q: q || undefined, scheme: scheme || undefined, value: value || undefined, limit: params.limit ?? 8 } },
    }),
  )
  return data.items
}

export const useAdminEntityMatches = (params: EntityMatchQuery | undefined, enabled = true) =>
  useQuery({
    queryKey: queryKeys.adminEntityMatches({
      type: params?.type,
      q: params?.q,
      scheme: params?.scheme,
      value: params?.value,
    }),
    queryFn: () => fetchEntityMatches(params as EntityMatchQuery),
    enabled: Boolean(enabled && params?.type && (params.q?.trim() || (params.scheme?.trim() && params.value?.trim()))),
    staleTime: 15_000,
  })

export const useAdminDictionaries = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.adminDictionaries,
    queryFn: () => unwrap<Dictionaries>(api.GET("/admin/dictionaries")),
    staleTime: 30 * 60_000,
    enabled,
  })

export const useAdminErrorReports = (status: ErrorReportStatus | undefined, page = 1, pageSize = 25) =>
  useQuery({
    queryKey: queryKeys.adminErrorReports(status, page),
    queryFn: () =>
      unwrap<{ items: ErrorReport[]; total: number }>(
        api.GET("/admin/error-reports", { params: { query: { status, page, page_size: pageSize } } }),
      ),
    placeholderData: keepPreviousData,
  })

export const useAdminUpdateErrorReport = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, status, resolution }: { reportId: string; status: ErrorReportStatus; resolution?: string }) =>
      unwrap<ErrorReport>(
        api.PATCH("/admin/error-reports/{reportId}", { params: { path: { reportId } }, body: { status, resolution } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "error-reports"] }),
  })
}

export const useAdminImports = (pollingMs?: number) =>
  useQuery({
    queryKey: queryKeys.adminImports,
    queryFn: () => unwrap<{ items: ImportJob[] }>(api.GET("/admin/imports")),
    refetchInterval: pollingMs,
  })

export const useAdminCreateImport = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) => {
      const form = new FormData()
      form.append("file", file)
      form.append("dry_run", String(dryRun))
      return unwrap<ImportJob>(
        api.POST("/admin/imports", {
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      )
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminImports }),
  })
}

export const useAdminReindex = () =>
  useMutation({
    mutationFn: (type?: SearchableType) =>
      unwrap<void>(api.POST("/admin/reindex", { body: type ? { type } : {} })),
  })

export const useAdminAudit = (page = 1, pageSize = 50, entityId?: string) =>
  useQuery({
    queryKey: queryKeys.adminAudit(page, entityId),
    queryFn: () =>
      unwrap<{ items: AuditEntry[]; total: number }>(
        api.GET("/admin/audit", { params: { query: { page, page_size: pageSize, entity_id: entityId } } }),
      ),
    placeholderData: keepPreviousData,
  })
